<template>
	<v-row>
		<v-col>
			<v-form v-model="formIsValid.contact" ref="contactForm">

				<ui-box
						:form="$refs.contactForm"
						:eager="!!localVenue.id"
						new-forms
				>

					<form-title title="Place">
						The following details will be shown in passes to customers for location and
						support.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" lg="5">
							<v-text-field
									v-model="localVenue.address"
									class="rounded-lg"
									:disabled="!isAdmin"
									label="Address"
									outlined
									persistent-hint
									:readonly="!isAdmin"
									hint="placeholder"
							>
								<template v-slot:message>
									If your address is incorrect, <a
										@click.prevent="$refs.createMessageDialog.open()"
								>submit a change request</a>.
								</template>
							</v-text-field>
						</v-col>
						<v-col cols="12" lg="7">
							<v-row>
								<v-col cols="12" md="7" lg="6">
									<v-text-field
											v-model="localVenue.website"
											class="flex-grow-1 rounded-lg"
											label="Website"
											outlined
											:rules="rules.website"
									></v-text-field>
								</v-col>
								<v-col cols="12" md="5" lg="6">
									<vue-tel-input-vuetify
											ref="phone"
											v-model="localVenue.phone"
											append-icon="mdi-menu-down"
											class="flex-grow-1 rounded-lg"
											clearable
											:defaultCountry="'us'"
											label="Phone"
											mode="international"
											outlined
											:preferred-countries="['us', 'ca']"
											:rules="rules.phone"
											@input="phoneInputEvent"
											@keydown="phoneKeyEvent"
									></vue-tel-input-vuetify>
								</v-col>
							</v-row>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.description" ref="descriptionForm">

				<ui-box
						:form="$refs.descriptionForm"
						:eager="!!localVenue.id"
						new-forms
				>

					<form-title title="Description">
						Please provide information about your venue.
					</form-title>

					<form-divider inner/>

					<!-- Description -->
					<v-row>
						<v-col>
							<div class="align-center field-actions d-flex justify-end">

								<div
										class="grey--text ms-2 text-caption text--darken-1"
								>
									<template v-if="isGeneratingDescription">
										This may take a couple seconds.
									</template>
									<template v-else-if="disableAutoGeneratingDescription">
										Auto-generate is currently unavailable.
									</template>
									<template v-else>Auto-generate</template>
								</div>
								<v-btn
										aria-label="Auto-Generate Description"
										icon
										:disabled="!localVenue.name || !localVenue.address || disableAutoGeneratingDescription"
										:loading="isGeneratingDescription"
										@click="generateDescription"
								>
									<v-icon
											v-if="!localVenue.name || !localVenue.address || disableAutoGeneratingDescription"
											key="autogen-off"
									>
										mdi-autorenew-off
									</v-icon>
									<v-icon
											v-else
											key="autogen-on"
									>
										mdi-autorenew
									</v-icon>
								</v-btn>
							</div>
							<v-textarea
									v-model="localVenue.description"
									auto-grow
									class="flex-grow-1 rounded-lg"
									:disabled="isGeneratingDescription"
									hide-details="auto"
									label="Description"
									outlined
									:maxlength="descriptionMaxLength"
									counter
									rows="4"
									:rules="rules.description"
							></v-textarea>
						</v-col>
					</v-row>
					<!-- End: Description -->

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.categories" ref="categoriesForm">

				<ui-box
						:form="$refs.categoriesForm"
						:eager="!!localVenue.id"
						new-forms
				>

					<form-title title="Business Category">
						How would you best describe your business?
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<v-select
									v-model="selectedParentCategory"
									class="rounded-lg"
									hide-details
									item-text="name"
									item-value="id"
									:items="parentCategories"
									label="Category"
									outlined
							></v-select>
						</v-col>
					</v-row>

					<v-row v-if="selectedParentCategory && childCategories.length">
						<v-col>
							<v-select
									v-model="selectedChildCategories"
									class="rounded-lg"
									hide-details
									item-text="name"
									item-value="id"
									:items="childCategories"
									label="Subcategory"
									multiple
									outlined
									small-chips
							></v-select>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.hours" ref="hoursForm">

				<ui-box
						:form="$refs.hoursForm"
						:eager="!!localVenue.id"
						new-forms
				>

					<form-title title="Business Hours">
						<template v-if="localVenue.google_place_id">
							Hours are pulled from your Google Business Profile. Through this connection,
							any updates to your hours in Google will reflect on the pass.
						</template>
						<template v-else>
							Because this is a manually created Place,
							you may update the business hours for this listing.
							Updating here will reflect on any live passes this Place is on.
						</template>
					</form-title>

					<form-divider inner/>

					<business-hours
						place-id-field="google_place_id"
						:update-endpoint="`/merchant-portal/${localVenue.id}/settings/main/get-business-hours`"
						:venue="localVenue"></business-hours>

				</ui-box>

			</v-form>

			<form-divider/>

			<v-form v-model="formIsValid.pin" ref="pinForm">

				<ui-box
						:form="$refs.pinForm"
						:eager="!!localVenue.id"
						new-forms
				>

					<form-title title="Pin Number">
						Your customers will reference this number when redeeming some products like
						coupons, tickets and for checking in.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="8" md="6" lg="4">
							<bw-pin-input
									v-model="localVenue.pin_number"
							>
							</bw-pin-input>
						</v-col>
					</v-row>

				</ui-box>

			</v-form>

			<form-divider/>

			<ui-box
					class="mt-8"
					new-forms
			>

				<form-title title="Business Image">
					Please ensure the image provided is of high quality and is preferably in a
					horizontal layout; portrait orientations may not be suitable.<br/>Additionally,
					kindly refrain from using any logos as these may appear cutoff on different
					devices and pages.
				</form-title>

				<form-divider inner/>

				<v-row>
					<bw-image
							v-model="image"
							:aspect-ratio="calculateAR(image.cropperSettings.limitAR)"
							cover
							:upload-assignment="'venue'"
							:upload-assignment-id="localVenue.id"
					></bw-image>
				</v-row>

			</ui-box>

			<save-form
					:is-saving="isSaving"
					is-valid-text=""
					:refs="$refs"
					new-forms
					save-text="Save"
					@cancel-action="exit"
					@save-action="save"
			></save-form>

			<create-message
					ref="createMessageDialog"
					:recipients="newMessage.recipients"
					:users="[]"
					:new-message="newMessage"
					@resetMsg="resetMsg"
			></create-message>
		</v-col>
	</v-row>
</template>

<script>
import FormDivider from "../../../../Shared/UI/Form/FormDivider.vue";
import { calculateAR } from "../../../../Shared/helpers";

export default {
	name: 'EditVenue',
	components: { FormDivider },
	props: {
		categories: {
			type: Array,
			default: () => []
		},
		params: {
			type: Object,
			default: () => ({})
		},
		user: {
			type: Object,
			default: () => ({})
		},
		venue: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			disableAutoGeneratingDescription: false,
			formIsValid: {
				contact: false,
				description: false,
				categories: false,
				hours: false,
				pin: false,
				image: false
			},
			isGeneratingDescription: false,
			descriptionMaxLength: 5000,
			isSaving: false,
			localVenue: this.venue,
			newMessage: {},
			phoneIsValid: true,
			rules: {
				description: [
					v => {
						if(this.fieldIsEmpty(v)) return 'Description is required.';
						return v.length <= 5000 || 'Description must be less than 5000 characters.';
					}
				],
				phone: [
					(v) => {
						if(this.fieldIsEmpty(v)) return true;

						return this.phoneIsValid || 'Please enter a valid phone.';
					}
				],
				website: [
					v => {
						if(this.fieldIsEmpty(v)) return true;

						const protocolRegex = /^https?:\/\//;
						return protocolRegex.test(v) || 'Please include the whole website url including http(s)://'
					},
					v => {
						if(this.fieldIsEmpty(v)) return true;

						const fullRegex = /^https?:\/\/(?:www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,16}\b[-a-zA-Z0-9()@:%_+.~#?&\/=]*$/;
						return fullRegex.test(v) || 'Website url is invalid.'
					}
				]
			}
		};
	},
	computed: {
		childCategories() {
			return this.selectedParentCategory && this.categories.filter(x => x.parent_id === this.selectedParentCategory) || [];
		},
		image: {
			get() {
				let m = this.localVenue.media.find(x => x.location === 'Header');
				if(!m) {
					m = {
						location: 'Header',
						name: 'Header',
						url: '/images/placehold.co.png',
					}
				}
				m.cropperSettings = {
					limitAR: '2:1',
					x: 0,
					y: 0
				};
				return m;
			},
			set(v) {
				v.location = 'Header';
				let i = this.localVenue.media.findIndex(x => x.location === 'Header');
				if(i > -1)
					this.localVenue.media[i] = v;
				else
					this.localVenue.media.push(v);
			}
		},
		isAdmin() {
			return this.user?.is_admin || false;
		},
		parentCategories() {
			return this.categories.filter(x => !x.parent_id)
		},
		selectedChildCategories: {
			get() {
				return this.childCategories.filter(x => this.localVenue.categories.find(y => y === x.id)).map(x => x.id);
			},
			set(c) {
				this.localVenue.categories = [this.selectedParentCategory, ...c];
			}
		},
		selectedParentCategory: {
			get() {
				return this.parentCategories.find(x => this.localVenue.categories.find(y => y === x.id))?.id;
			},
			set(c) {
				this.localVenue.categories = [c];
			}
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this.beforeUnloadListener, { capture: true });
	},
	mounted() {
		if(this.venue.media.length === 0) {
			this.$set(this.localVenue.media, 0, {
				location: 'Header',
				name: 'Header',
				url: '/images/placehold.co.png',
				cropperSettings: {
					limitAR: '2:1',
					x: 0,
					y: 0
				}
			});
		}

		// make sure cropper settings are being setup right
		for (let im of this.localVenue.media) {
			if(im.location === 'Header') {
				this.$set(im, 'cropperSettings', {
					limitAR: '2:1',
					x: 0,
					y: 0
				});
			}
			else if(im.location === 'Square') {
				this.$set(im, 'cropperSettings', {
					limitAR: '4:3',
					x: 0,
					y: 0
				});
			}
		}

		this.resetMsg();
	},
	methods: {
		calculateAR,
		exit() {
			if(this.params?.invite?.id)
				return window.location.href = `/merchant-portal/${this.localVenue.id}/invites/view/invite/${this.params.invite.id}`;

			window.location.href = `/merchant-portal/${this.localVenue.id}/dashboard/main/view-dashboard`;
		},
		fieldIsEmpty(v) {
			return !v || v.trim() === '';
		},
		generateDescription() {
			this.errors = {};

			if(Object.keys(this.errors).length) {
				window.EventBus.$emit('system-messages', [
					{
						message: 'You must enter a name and address.',
						messageType: 'error'
					}
				]);
				return false;
			}

			this.isGeneratingDescription = true;

			// this.localVenue.description = '';

			axios.put(`/merchant-portal/${this.localVenue.id}/settings/main/generate-description`, {
				name: this.localVenue.name,
				address: this.localVenue.address,
				maxLength: this.descriptionMaxLength
			})
					.then(response => {
						let { data } = response;

						if(data.length > this.descriptionMaxLength) {
							data = data.substring(0, this.descriptionMaxLength);
						}

						this.localVenue.description = data;
						this.isGeneratingDescription = false;
					})
					.catch(err => {
						console.error('Auto-Generate Description Error: ', err);
						this.isGeneratingDescription = false;
						this.disableAutoGeneratingDescription = true;
					});
		},
		async onSubmit() {
			if(!this.formIsValid)
				return;
			this.save();
		},
		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			if(formattedNumber && !valid) this.formIsValid.contact = false;

			// For field validation rule
			this.phoneIsValid = !formattedNumber || valid;
		},
		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},
		resetMsg() {
			let preMessage = '';
			const venueLink = window.location.protocol + '//' + window.location.host + '/admin/' + this.localVenue.partner_id + '/venue/' + this.localVenue.id + '#contact';
			if(this.params?.partner?.id)
				preMessage += `<p>Partner: ${this.params?.partner?.name || ''} </p>`;
			preMessage += `
					<p>Merchant: ${this.localVenue.name}</p>
					<p>Contact: ${this.user.name} ${this.user.email} </p>
					<p>Venue Link: <a target="_blank" href="${venueLink}">${venueLink}</a></p>
					<p>------- Write your new address below this line --------</p>
				`;
			this.newMessage = {
				disableRecipients: true,
				hideAttachments: true,
				lockSubject: true,
				message: '',
				preMessage,
				messsageLabel: 'New Address',
				recipients: [this.params?.merchantUser],
				rows: 1,
				subject: 'Address Change Request',
			};
			this.$refs.createMessageDialog.close();
		},
		async save() {
			this.isSaving = true;
			return new Promise(async resolve => {

				let url = `/merchant-portal/${this.localVenue.id}/settings/main/save-settings`;
				axios.put(url, this.localVenue).then(response => {
					if(response.data?.venue?.media) { // update media assignment ids to prevent duplicates
						for (let media of response.data.venue.media) {
							let f = this.localVenue.media.find(x => x.location === media.location);
							if(f) f.id = media.id;
						}
					}

					window.userpilot?.track(`venue_submitted`, { venue_id: this.localVenue.id });

					window.EventBus.$emit('system-messages', [
						{
							message: `${this.venue.name} settings saved`,
							messageType: 'success',
							showOverlay: true
						}
					]);

					resolve();
					if (this.params?.invite?.id) {
						return window.location.href = `/merchant-portal/${this.localVenue.id}/invites/view/invite/${this.params.invite.id}`;
					} else {
						window.location.reload();
					}
				}).catch(error => {
					window.EventBus.$emit('system-messages', [
						{
							message: error.message,
							messageType: 'error'
						}
					]);
					resolve();
				}).finally(() => {
					this.isSaving = false;
				});
			});
		},
		validateForm() {
			return this.$refs?.settingsForm?.validate() || false;
		}
	}
};
</script>

<style lang="scss" scoped>
.v-input--checkbox {
	margin-top: 4px !important;
}

</style>